import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';

const toDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const parseDate = (value) => {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const AvailabilityDatePicker = ({ villa, checkIn, checkOut, onChange }) => {
  const { t } = useTranslation();
  const today = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);

  const [visibleMonth, setVisibleMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [rates, setRates] = useState({});
  const [ratesLoading, setRatesLoading] = useState(false);
  const [hoverDate, setHoverDate] = useState(null);

  useEffect(() => {
    if (!villa) {
      setRates({});
      return;
    }

    const loadRates = async () => {
      setRatesLoading(true);
      try {
        const startDate = toDateStr(today);
        const end = addDays(today, 365);
        const endDate = toDateStr(end);
        const response = await api.getSmoobuRates({ villa, startDate, endDate });
        setRates(response.rates || {});
      } catch {
        setRates({});
      } finally {
        setRatesLoading(false);
      }
    };

    loadRates();
  }, [villa, today]);

  const isPast = (dateStr) => parseDate(dateStr) < today;

  const isAvailableDay = (dateStr) => {
    if (isPast(dateStr)) return false;
    const day = rates[dateStr];
    if (!day) return true;
    return day.available !== 0;
  };

  const nightsBetween = (startStr, endStr) => {
    const start = parseDate(startStr);
    const end = parseDate(endStr);
    const nights = Math.round((end - start) / (1000 * 60 * 60 * 24));
    return nights;
  };

  const isRangeAvailable = (startStr, endStr) => {
    if (!startStr || !endStr) return false;
    const nights = nightsBetween(startStr, endStr);
    if (nights <= 0) return false;

    for (let i = 0; i < nights; i += 1) {
      const dayStr = toDateStr(addDays(parseDate(startStr), i));
      if (!isAvailableDay(dayStr)) return false;
    }
    return true;
  };

  const isInRange = (dateStr) => {
    if (!checkIn) return false;
    const endStr = hoverDate && !checkOut ? hoverDate : checkOut;
    if (!endStr) return dateStr === checkIn;

    const start = parseDate(checkIn);
    const end = parseDate(endStr);
    const current = parseDate(dateStr);
    const min = start < end ? start : end;
    const max = start < end ? end : start;
    return current >= min && current <= max;
  };

  const handleDayClick = (dateStr) => {
    if (!isAvailableDay(dateStr)) return;

    // Both dates set — start a new range from the clicked day
    if (checkIn && checkOut) {
      onChange({ checkIn: dateStr, checkOut: '' });
      return;
    }

    // Only check-in set — picking check-out or changing selection
    if (checkIn && !checkOut) {
      // Same day again → clear selection
      if (dateStr === checkIn) {
        onChange({ checkIn: '', checkOut: '' });
        return;
      }

      // Earlier day → move check-in
      if (parseDate(dateStr) < parseDate(checkIn)) {
        onChange({ checkIn: dateStr, checkOut: '' });
        return;
      }

      // Later day with all nights available → set check-out
      if (isRangeAvailable(checkIn, dateStr)) {
        onChange({ checkIn, checkOut: dateStr });
        return;
      }

      // Later day but blocked nights in between → restart from clicked day
      onChange({ checkIn: dateStr, checkOut: '' });
      return;
    }

    // No dates yet
    onChange({ checkIn: dateStr, checkOut: '' });
  };

  const handleClearDates = () => {
    onChange({ checkIn: '', checkOut: '' });
    setHoverDate(null);
  };

  const monthLabel = visibleMonth.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  const calendarDays = useMemo(() => {
    const year = visibleMonth.getFullYear();
    const month = visibleMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const startOffset = (firstDay.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const cells = [];
    for (let i = 0; i < startOffset; i += 1) {
      cells.push(null);
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      cells.push(toDateStr(new Date(year, month, day)));
    }
    return cells;
  }, [visibleMonth]);

  const canGoPrev =
    visibleMonth.getFullYear() > today.getFullYear() ||
    (visibleMonth.getFullYear() === today.getFullYear() && visibleMonth.getMonth() > today.getMonth());

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() =>
            setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1, 1))
          }
          disabled={!canGoPrev}
          className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="font-sofia font-semibold text-black">{monthLabel}</div>
        <button
          type="button"
          onClick={() =>
            setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1))
          }
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {ratesLoading && (
        <p className="text-sm font-sofia text-[#81736d] animate-pulse">
          {t('booking.loadingAvailability', 'Loading availability...')}
        </p>
      )}

      <div className="grid grid-cols-7 gap-0.5 sm:gap-1 text-center text-[10px] sm:text-xs font-sofia text-text-secondary mb-1">
        {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((label) => (
          <div key={label}>{label}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
        {calendarDays.map((dateStr, index) => {
          if (!dateStr) {
            return <div key={`empty-${index}`} />;
          }

          const available = isAvailableDay(dateStr);
          const selected = dateStr === checkIn || dateStr === checkOut;
          const inRange = isInRange(dateStr);

          return (
            <button
              key={dateStr}
              type="button"
              disabled={!available}
              onClick={() => handleDayClick(dateStr)}
              onMouseEnter={() => checkIn && !checkOut && available && setHoverDate(dateStr)}
              onMouseLeave={() => setHoverDate(null)}
              className={[
                'h-8 sm:h-10 rounded-md sm:rounded-lg text-xs sm:text-sm font-sofia transition-colors',
                available
                  ? 'hover:bg-[#81736d]/20 cursor-pointer text-black'
                  : 'text-gray-300 line-through cursor-not-allowed bg-gray-50',
                inRange && available ? 'bg-[#81736d]/15' : '',
                selected && available ? 'bg-[#81736d] text-white hover:bg-[#81736d]' : '',
              ].join(' ')}
            >
              {parseDate(dateStr).getDate()}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-4 text-xs font-sofia text-text-secondary">
        <span className="inline-flex items-center gap-2">
          <span className="w-3 h-3 rounded bg-[#81736d]" />
          {t('booking.availableDay', 'Available')}
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="w-3 h-3 rounded bg-gray-100 border border-gray-200" />
          {t('booking.unavailableDay', 'Unavailable')}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-gray-200 p-3">
          <div className="text-xs text-text-secondary font-sofia">{t('booking.checkIn')}</div>
          <div className="font-sofia font-medium">{checkIn || '—'}</div>
        </div>
        <div className="rounded-lg border border-gray-200 p-3">
          <div className="text-xs text-text-secondary font-sofia">{t('booking.checkOut')}</div>
          <div className="font-sofia font-medium">{checkOut || '—'}</div>
        </div>
      </div>

      {(checkIn || checkOut) && (
        <button
          type="button"
          onClick={handleClearDates}
          className="text-sm font-sofia text-[#81736d] underline underline-offset-2 hover:text-[#81736d] transition-colors"
        >
          {t('booking.clearDates', 'Clear dates')}
        </button>
      )}
    </div>
  );
};

export default AvailabilityDatePicker;
